/**
 * SnapshotEditWorkspace (Detailed Campaign Edit)
 *
 * Store-centric workspace for editing campaign snapshots:
 * 1. EDIT - Store-first table with search, multi-select, bulk actions
 * 2. REVIEW - Preview diff grouped by action type
 * 3. CONFIRM - Apply mutations atomically
 *
 * Key principles:
 * - Store-first: Row = Store, not Store×Format×Operator
 * - Two-level interaction: Collapsed store view + expanded detail
 * - Bulk-first: Multi-select enables bulk actions
 * - Search: Filter by store name, code, town
 * - Snapshot-first: edits modify the saved snapshot, not live packs
 * - Atomic mutations: all changes committed in one transaction
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    AlertTriangle,
    CheckCircle,
    Edit3,
    Eye,
    CheckSquare,
    Plus,
    Minus,
    ArrowRight,
    Loader2,
    Search,
    ChevronDown,
    ChevronRight,
    Trash2,
    Users,
    Package,
    Info,
    X,
    MoreVertical,
} from 'lucide-react';
import { AddStoreModal } from './AddStoreModal';
import { AddStoreManualModal } from './AddStoreManualModal';
import {
    coverageToQuantity,
    formatLimitedCapacityAvailabilityHint,
    getEditablePopadCoverageOptions,
    getMaxLimitedCapacityUnits,
    nearestPopadCoverageBand,
    parsePopadFormatsResponse,
    quantityToCoveragePercent,
    type LimitedCapacityFormat,
} from '@/lib/popadAvailability';
import {
    resolveManualCampaignBooking,
    type BookingConfiguration,
} from '@/lib/resolveManualCampaignBooking';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { apiFetch } from '@/lib/api';

import type {
    SnapshotMutation,
    SnapshotDiff,
    StoreEditRow,
    FormatAssignment,
    EditWorkspaceStage,
    ApplyMutationsResult,
} from '@/types/snapshot-edit-types';
import type { GroupsViewResponse, UnifiedGroup } from '@shared/unified-groups';

interface SnapshotEditWorkspaceProps {
    campaignId: number;
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

interface PackContext {
    packId: number;
    packName: string;
}

interface SnapshotDataResult {
    stores: StoreEditRow[];
    packContext: PackContext[];
    // Add Store mode detection fields
    revisionNumber: number;
    hasCustomisedPacks: boolean;
    campaignType: 'pack' | 'manual';
}

async function fetchSnapshotData(campaignId: number): Promise<SnapshotDataResult> {
    const response = await apiFetch(`/api/campaign-snapshots/${campaignId}/details`);
    if (!response.ok) {
        throw new Error('Failed to fetch snapshot data');
    }
    const data = await response.json();

    // Transform to store-centric structure
    const packs = data.packs || [];
    const packContext: PackContext[] = packs.map((p: any) => ({
        packId: p.packId,
        packName: p.packName || `Pack ${p.packId}`,
    }));

    // Group assignments by store
    const storeMap = new Map<number, StoreEditRow>();

    for (const pack of packs) {
        const assignments = pack.storeFormatAssignments || [];
        for (const a of assignments) {
            const storeId = a.storeId;
            let store = storeMap.get(storeId);

            if (!store) {
                store = {
                    storeId,
                    storeName: a.storeName || `Store ${storeId}`,
                    storeCode: a.storeCode,
                    storeAddress: a.storeAddress,
                    town: a.town || a.storeRegion,
                    formatCount: 0,
                    operatorCount: 0,
                    totalQuantity: 0,
                    status: 'clean',
                    formatSummary: '',
                    operatorSummary: '',
                    assignments: [],
                };
                storeMap.set(storeId, store);
            }

            const assignment: FormatAssignment = {
                key: `${pack.packId}-${a.storeId}-${a.mediaFormatId}`,
                packId: pack.packId,
                packName: pack.packName || `Pack ${pack.packId}`,
                storeId: a.storeId,
                formatId: a.mediaFormatId,
                formatName: a.mediaFormatName || `Format ${a.mediaFormatId}`,
                operatorId: a.operatorId || 0,
                operatorName: a.operatorName || 'Unassigned',
                quantity: a.quantity || 1,
                originalQuantity: a.quantity || 1,
                originalOperatorId: a.operatorId || 0,
            };

            store.assignments.push(assignment);
        }
    }

    // Compute summaries for each store
    for (const store of storeMap.values()) {
        const formatIds = new Set(store.assignments.map(a => a.formatId));
        const operatorIds = new Set(store.assignments.map(a => a.operatorId));

        store.formatCount = formatIds.size;
        store.operatorCount = operatorIds.size;
        store.totalQuantity = store.assignments.reduce((sum, a) => sum + a.quantity, 0);
        store.formatSummary = `${store.formatCount} format${store.formatCount !== 1 ? 's' : ''}`;
        store.operatorSummary = `${store.operatorCount} operator${store.operatorCount !== 1 ? 's' : ''}`;

        // Check for issues (unassigned operators)
        const hasUnassigned = store.assignments.some(a => a.operatorId === 0);
        store.status = hasUnassigned ? 'warning' : 'clean';
    }

    return {
        stores: Array.from(storeMap.values()).sort((a, b) => a.storeName.localeCompare(b.storeName)),
        packContext,
        // Add Store mode detection fields from API
        revisionNumber: data.revisionNumber ?? 1,
        hasCustomisedPacks: data.hasCustomisedPacks ?? false,
        campaignType: data.campaignType ?? 'pack',
    };
}

async function fetchOperators(): Promise<{ id: number; name: string }[]> {
    const response = await apiFetch('/api/users/operators');
    if (!response.ok) return [];
    const data = await response.json();
    const items = Array.isArray(data) ? data : (data?.data ?? []);
    return items
        .map((op: { id: number; name?: string; username?: string }) => ({
            id: op.id,
            name: op.name || op.username || 'Unknown',
        }))
        .sort((a: { id: number; name: string }, b: { id: number; name: string }) =>
            a.name.localeCompare(b.name));
}

async function fetchFormats(campaignId: number): Promise<{ id: number; name: string }[]> {
    // Get available formats from the campaign's retailer
    const response = await apiFetch(`/api/campaigns/${campaignId}`);
    if (!response.ok) return [];
    const campaign = await response.json();

    if (!campaign.retailerId) return [];

    // Use the correct endpoint: /api/media-formats with retailerId query
    const formatsRes = await apiFetch(`/api/media-formats?retailerId=${campaign.retailerId}`);
    if (!formatsRes.ok) return [];
    const data = await formatsRes.json();
    // Handle both paginated and non-paginated responses
    const formats = data.formats || data;
    return (formats || []).map((f: any) => ({ id: f.id, name: f.name }));
}

async function generateDiff(campaignId: number, mutations: SnapshotMutation[]): Promise<SnapshotDiff> {
    const response = await apiFetch(`/api/campaigns/${campaignId}/snapshot/diff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mutations }),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.details || 'Failed to generate diff');
    }
    return response.json();
}

async function applyMutations(
    campaignId: number,
    mutations: SnapshotMutation[],
    reason?: string
): Promise<ApplyMutationsResult> {
    const response = await apiFetch(`/api/campaigns/${campaignId}/snapshot/mutations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mutations, reason }),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.details || 'Failed to apply mutations');
    }
    return response.json();
}

// ============================================================================
// Component
// ============================================================================

export function SnapshotEditWorkspace({
    campaignId,
    isOpen,
    onClose,
    onSuccess,
}: SnapshotEditWorkspaceProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // State for retailerId, needed for AddStoreModal and fetching formats
    const [retailerId, setRetailerId] = useState<number | null>(null);

    // Stage state
    const [stage, setStage] = useState<EditWorkspaceStage>('EDIT');

    // Search state
    const [searchStore, setSearchStore] = useState('');
    const [searchCode, setSearchCode] = useState('');
    const [searchTown, setSearchTown] = useState('');

    // Selection state
    const [selectedStores, setSelectedStores] = useState<Set<number>>(new Set());
    const [selectedFormats, setSelectedFormats] = useState<Set<string>>(new Set()); // keyed by assignment key

    // Expanded stores
    const [expandedStores, setExpandedStores] = useState<Set<number>>(new Set());

    // Edited assignments (key -> FormatAssignment)
    const [editedAssignments, setEditedAssignments] = useState<Map<string, FormatAssignment>>(new Map());

    // Pending bulk mutations
    const [pendingMutations, setPendingMutations] = useState<SnapshotMutation[]>([]);

    // Review state
    const [diff, setDiff] = useState<SnapshotDiff | null>(null);
    const [confirmReason, setConfirmReason] = useState('');

    // Add format modal
    const [showAddFormatModal, setShowAddFormatModal] = useState(false);
    const [selectedFormatToAdd, setSelectedFormatToAdd] = useState<number | null>(null);

    // Add store modal
    const [isAddStoreModalOpen, setIsAddStoreModalOpen] = useState(false);
    const [isAddStoreManualModalOpen, setIsAddStoreManualModalOpen] = useState(false);

    // Removed stores (staged for removal)
    const [removedStores, setRemovedStores] = useState<Set<number>>(new Set());

    // Removed formats (staged for removal) - keyed by assignment key (packId-storeId-formatId)
    const [removedFormats, setRemovedFormats] = useState<Set<string>>(new Set());

    // ========================================================================
    // Queries
    // ========================================================================

    // Fetch campaign data to get retailerId
    const { data: campaignData } = useQuery({
        queryKey: ['campaign', campaignId],
        queryFn: async () => {
            const response = await apiFetch(`/api/campaigns/${campaignId}`);
            if (!response.ok) throw new Error('Failed to fetch campaign data');
            return await response.json();
        },
        enabled: isOpen,
    });

    useEffect(() => {
        if (campaignData?.retailerId) {
            setRetailerId(campaignData.retailerId);
        }
    }, [campaignData]);

    const { data: snapshotData, isLoading: isLoadingData, error: dataError } = useQuery({
        queryKey: ['snapshot-edit', campaignId],
        queryFn: () => fetchSnapshotData(campaignId),
        enabled: isOpen,
    });

    const { data: groupsViewData } = useQuery<GroupsViewResponse>({
        queryKey: ['/api/campaigns', campaignId, 'groups-view'],
        queryFn: async () => {
            const response = await apiFetch(`/api/campaigns/${campaignId}/groups-view`);
            if (!response.ok) throw new Error('Failed to fetch groups view');
            return response.json();
        },
        enabled: isOpen,
        staleTime: 0,
        refetchOnMount: 'always',
    });

    const { data: operators = [] } = useQuery({
        queryKey: ['/api/users/operators'],
        queryFn: fetchOperators,
        enabled: isOpen,
    });

    const operatorOptions = useMemo(() => {
        const byId = new Map<number, { id: number; name: string }>();
        for (const op of operators) {
            byId.set(op.id, op);
        }
        // Keep currently assigned operators visible even if inactive
        for (const store of snapshotData?.stores ?? []) {
            for (const assignment of store.assignments) {
                if (assignment.operatorId > 0 && !byId.has(assignment.operatorId)) {
                    byId.set(assignment.operatorId, {
                        id: assignment.operatorId,
                        name: assignment.operatorName,
                    });
                }
            }
        }
        return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [operators, snapshotData?.stores]);

    const { data: availableFormats = [] } = useQuery({
        queryKey: ['campaign-formats', campaignId, retailerId], // Depend on retailerId
        queryFn: () => fetchFormats(campaignId),
        enabled: isOpen && retailerId !== null, // Only enable if retailerId is known
    });

    // Fetch all limited-capacity formats (PopAds, screen slots, etc.)
    const { data: limitedCapacityFormats = [] } = useQuery<LimitedCapacityFormat[]>({
        queryKey: ['limited-capacity-formats', retailerId],
        queryFn: async () => {
            const url = retailerId != null
                ? `/api/popad/formats?retailerId=${retailerId}`
                : '/api/popad/formats';
            const response = await apiFetch(url);
            if (!response.ok) return [];
            const data = await response.json();
            return parsePopadFormatsResponse(data);
        },
        enabled: isOpen,
    });

    const limitedFormatIds = useMemo(
        () => new Set(limitedCapacityFormats.map((format) => format.id)),
        [limitedCapacityFormats],
    );

    const formatBookingById = useMemo(() => {
        const map = new Map<number, BookingConfiguration>();
        for (const format of limitedCapacityFormats) {
            map.set(format.id, resolveManualCampaignBooking(format));
        }
        return map;
    }, [limitedCapacityFormats]);

    // Fetch limited-capacity availability for this campaign's cycle
    const { data: popadAvailability } = useQuery<{
        cycleId: number;
        availability: Array<{
            storeId: number;
            storeName: string;
            mediaFormatId: number;
            capacity: number;
            allocatedUnits: number;
            allocatedPercent: number;
            remainingPercent: number;
            remainingUnits: number;
        }>;
    }>({
        queryKey: ['popad-availability', campaignData?.cycleId],
        queryFn: async () => {
            if (!campaignData?.cycleId) throw new Error('No cycle ID');
            const response = await apiFetch(`/api/popad/availability/${campaignData.cycleId}`);
            if (!response.ok) return { cycleId: 0, availability: [] };
            return response.json();
        },
        enabled: isOpen && !!campaignData?.cycleId,
    });

    // Create lookup map for POPAd availability: "storeId-formatId" -> availability
    const popadAvailabilityMap = useMemo(() => {
        const map = new Map<string, {
            capacity: number;
            allocatedPercent: number;
            remainingPercent: number;
            remainingUnits: number;
        }>();
        if (popadAvailability?.availability) {
            for (const avail of popadAvailability.availability) {
                map.set(`${avail.storeId}-${avail.mediaFormatId}`, {
                    capacity: avail.capacity,
                    allocatedPercent: avail.allocatedPercent,
                    remainingPercent: avail.remainingPercent,
                    remainingUnits: avail.remainingUnits,
                });
            }
        }
        return map;
    }, [popadAvailability]);

    // Helper to check if a format uses shared limited-capacity booking rules
    const isLimitedCapacityFormat = useCallback(
        (formatId: number) => limitedFormatIds.has(formatId),
        [limitedFormatIds],
    );

    // Helper to get limited-capacity availability for a store-format combination
    const getLimitedCapacityAvailability = useCallback((storeId: number, formatId: number) => {
        return popadAvailabilityMap.get(`${storeId}-${formatId}`);
    }, [popadAvailabilityMap]);

    // ========================================================================
    // Mutations
    // ========================================================================

    const diffMutation = useMutation({
        mutationFn: (mutations: SnapshotMutation[]) => generateDiff(campaignId, mutations),
        onSuccess: (result) => {
            setDiff(result);
            setStage('REVIEW');
        },
        onError: (error: Error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        },
    });

    const applyMutation = useMutation({
        mutationFn: ({ mutations, reason }: { mutations: SnapshotMutation[]; reason?: string }) =>
            applyMutations(campaignId, mutations, reason),
        onSuccess: (result) => {
            toast({
                title: 'Success',
                description: `${result.changesApplied} changes applied. New revision: ${result.newRevisionNumber}`,
            });
            queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
            queryClient.invalidateQueries({ queryKey: ['campaign-snapshots', campaignId] });
            queryClient.invalidateQueries({ queryKey: ['snapshot-edit', campaignId] });
            queryClient.invalidateQueries({ queryKey: ['popad-availability'] });
            queryClient.invalidateQueries({ queryKey: ['popad-availability', campaignData?.cycleId] });
            queryClient.invalidateQueries({ queryKey: ['limited-capacity-formats'] });
            // Invalidate API-style keys used by Header and Assignments tab
            queryClient.invalidateQueries({ queryKey: ['/api/campaigns', campaignId] });
            onSuccess?.();
            handleClose();
        },
        onError: (error: Error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        },
    });

    // ========================================================================
    // Filtered stores (search)
    // ========================================================================

    const filteredStores = useMemo(() => {
        if (!snapshotData?.stores) return [];

        return snapshotData.stores.filter(store => {
            // Exclude removed stores from display
            if (removedStores.has(store.storeId)) return false;

            const matchesStore = !searchStore ||
                store.storeName.toLowerCase().includes(searchStore.toLowerCase());
            const matchesCode = !searchCode ||
                (store.storeCode?.toLowerCase().includes(searchCode.toLowerCase()));
            const matchesTown = !searchTown ||
                (store.town?.toLowerCase().includes(searchTown.toLowerCase()));

            return matchesStore && matchesCode && matchesTown;
        });
    }, [snapshotData?.stores, searchStore, searchCode, searchTown, removedStores]);

    // ========================================================================
    // Get display data with edits applied
    // ========================================================================

    // Snapshot edit row model:
    // - StoreEditRow is the top-level "row" in the table (store-centric).
    // - FormatAssignment is the expanded row inside each store.
    // Grouping identifiers available in this dataset:
    // - StoreEditRow.storeId
    // - FormatAssignment.packId / packName (no groupId on rows).
    const displayStores = useMemo(() => {
        const stores = [...filteredStores];

        // Add virtual stores from pending ADD_STORE mutations
        const addedStoreMutations = pendingMutations.filter(m => m.type === 'ADD_STORE');
        for (const m of addedStoreMutations) {
            if (m.storeId && !removedStores.has(m.storeId)) {
                // Check if already in display (shouldn't be, but for safety)
                if (stores.some(s => s.storeId === m.storeId)) continue;

                const assignments: FormatAssignment[] = (m.formats || []).map(f => ({
                    key: `virtual-${f.packId}-${m.storeId}-${f.mediaFormatId}`,
                    packId: f.packId,
                    packName: snapshotData?.packContext.find(pc => pc.packId === f.packId)?.packName || `Pack ${f.packId}`,
                    storeId: m.storeId!,
                    formatId: f.mediaFormatId,
                    formatName: f.mediaFormatName,
                    operatorId: f.operatorId,
                    operatorName: f.operatorName,
                    quantity: f.quantity,
                    originalQuantity: 0,
                    originalOperatorId: 0,
                    isModified: true
                }));

                stores.push({
                    storeId: m.storeId,
                    storeName: m.storeName || `Store ${m.storeId}`,
                    storeCode: m.storeCode, // Added storeCode
                    storeAddress: m.storeAddress, // Added storeAddress
                    town: m.town, // Added town
                    formatCount: assignments.length,
                    operatorCount: new Set(assignments.map(a => a.operatorId)).size,
                    totalQuantity: assignments.reduce((sum, a) => sum + a.quantity, 0),
                    status: 'mixed', // Virtual stores are always "modified"
                    formatSummary: `${assignments.length} format${assignments.length !== 1 ? 's' : ''}`,
                    operatorSummary: '', // Will be computed below if needed
                    assignments,
                    isExpanded: false
                });
            }
        }

        return stores.map(store => {
            // filter out removed format assignments
            const activeAssignments = store.assignments.filter(
                assignment => !removedFormats.has(assignment.key)
            );

            const updatedAssignments = activeAssignments.map(assignment => {
                const edited = editedAssignments.get(assignment.key);
                if (edited) {
                    return {
                        ...edited,
                        isModified: edited.quantity !== assignment.originalQuantity ||
                            edited.operatorId !== assignment.originalOperatorId,
                    };
                }
                return assignment;
            });

            const hasModified = updatedAssignments.some(a => a.isModified);

            // Recompute summaries for the display store
            const formatIds = new Set(updatedAssignments.map(a => a.formatId));
            const operatorIds = new Set(updatedAssignments.map(a => a.operatorId));
            const totalQuantity = updatedAssignments.reduce((sum, a) => sum + a.quantity, 0);
            const hasUnassigned = updatedAssignments.some(a => a.operatorId === 0);

            let status: StoreEditRow['status'] = 'clean';
            if (hasModified) status = 'mixed';
            else if (hasUnassigned) status = 'warning';

            return {
                ...store,
                assignments: updatedAssignments,
                formatCount: formatIds.size,
                operatorCount: operatorIds.size,
                totalQuantity: totalQuantity,
                formatSummary: `${formatIds.size} format${formatIds.size !== 1 ? 's' : ''}`,
                operatorSummary: `${operatorIds.size} operator${operatorIds.size !== 1 ? 's' : ''}`,
                status: status,
            };
        }).filter(store => store.assignments.length > 0); // Hide stores with no remaining formats
    }, [filteredStores, editedAssignments, removedFormats, pendingMutations, snapshotData?.packContext, removedStores]);

    // ========================================================================
    // Group-aware presentation (read-only)
    // ========================================================================

    const orderedGroups = useMemo(() => {
        const authored = groupsViewData?.authoredGroups ?? [];
        const derived = groupsViewData?.derivedGroups ?? [];
        return [...authored, ...derived];
    }, [groupsViewData]);

    const groupIndex = useMemo(() => {
        const groupByKey = new Map<string, UnifiedGroup>();
        const storeToGroupKeys = new Map<number, string[]>();

        for (const group of orderedGroups) {
            groupByKey.set(group.groupKey, group);
            for (const storeId of group.storeIds) {
                const existing = storeToGroupKeys.get(storeId);
                if (existing) {
                    existing.push(group.groupKey);
                } else {
                    storeToGroupKeys.set(storeId, [group.groupKey]);
                }
            }
        }

        return { groupByKey, storeToGroupKeys };
    }, [orderedGroups]);

    const groupedStoreSections = useMemo(() => {
        if (orderedGroups.length === 0) return [];
        return orderedGroups.map(group => {
            const storeSet = new Set(group.storeIds);
            const stores = displayStores.filter(store => storeSet.has(store.storeId));
            return { group, stores };
        });
    }, [orderedGroups, displayStores]);

    const ungroupedStores = useMemo(() => {
        if (orderedGroups.length === 0) return [];
        return displayStores.filter(store => !groupIndex.storeToGroupKeys.has(store.storeId));
    }, [displayStores, groupIndex, orderedGroups.length]);

    // ========================================================================
    // Build mutations from edits + pending bulk mutations
    // ========================================================================

    const buildMutations = useCallback((): SnapshotMutation[] => {
        const mutations: SnapshotMutation[] = [...pendingMutations];

        // Add row-level edits
        for (const [, edited] of editedAssignments) {
            const { packId, storeId, formatId } = edited;

            // Find original
            const original = snapshotData?.stores
                .flatMap(s => s.assignments)
                .find(a => a.key === edited.key);

            // If it's a new assignment from an ADD_STORE mutation, it won't have an 'original' in snapshotData.
            // We need to check if it's part of a pending ADD_STORE mutation.
            const isPartOfPendingAddStore = pendingMutations.some(
                m => m.type === 'ADD_STORE' && m.storeId === storeId && m.formats?.some(f => f.packId === packId && f.mediaFormatId === formatId)
            );

            if (!original && !isPartOfPendingAddStore) continue; // Skip if not original and not part of a pending add

            // For existing assignments, check quantity change
            if (original && edited.quantity !== original.originalQuantity) {
                mutations.push({
                    type: 'UPDATE_QUANTITY',
                    packId,
                    storeId,
                    formatId,
                    quantity: edited.quantity,
                });
            }

            // For existing assignments, check operator change
            if (original && edited.operatorId !== original.originalOperatorId) {
                mutations.push({
                    type: 'REASSIGN_OPERATOR',
                    packId,
                    storeId,
                    formatId,
                    operatorId: edited.operatorId,
                    operatorName: edited.operatorName,
                });
            }
            // If it's part of a pending ADD_STORE, its quantity and operator are already set in the ADD_STORE mutation.
            // We don't generate separate UPDATE_QUANTITY or REASSIGN_OPERATOR for them unless they are modified *after* the ADD_STORE.
            // This logic might need refinement depending on how ADD_STORE mutations are structured and if they can be further edited.
            // For now, assuming ADD_STORE sets initial values, and subsequent edits would apply to the 'virtual' assignment.
            // The current `editedAssignments` map will correctly track these changes.
            // If an assignment is part of an ADD_STORE mutation, its 'originalQuantity' and 'originalOperatorId' would be 0.
            // So, if edited.quantity !== 0 or edited.operatorId !== 0, it implies a change from the 'virtual' initial state.
            // However, the ADD_STORE mutation itself already contains the initial quantity and operator.
            // This part needs careful consideration to avoid duplicate or conflicting mutations.
            // For simplicity, if an assignment is part of an ADD_STORE, we assume its values are already captured in that ADD_STORE mutation.
            // If the user *edits* a format within a *newly added store*, those edits should ideally modify the ADD_STORE mutation itself,
            // or be treated as subsequent UPDATE/REASSIGN mutations on the newly added format.
            // The current `buildMutations` only checks against `original` from `snapshotData`.
            // To handle edits on newly added stores/formats, we'd need to modify the `ADD_STORE` mutation in `pendingMutations` directly,
            // or generate `UPDATE_QUANTITY`/`REASSIGN_OPERATOR` mutations that apply to the *newly added* format.
            // For now, let's assume edits on newly added stores/formats are not separately tracked here,
            // as their initial state is defined by the ADD_STORE mutation itself.
        }

        return mutations;
    }, [editedAssignments, pendingMutations, snapshotData?.stores]);

    // Formats available to add (not already in ALL selected stores)
    const formatsNotInSelection = useMemo(() => {
        if (selectedStores.size === 0 || availableFormats.length === 0) return availableFormats;

        // Find formats that exist in at least one selected store
        const formatsInSelectedStores = new Set<number>();
        for (const store of displayStores) {
            if (selectedStores.has(store.storeId)) {
                for (const assignment of store.assignments) {
                    formatsInSelectedStores.add(assignment.formatId);
                }
            }
        }

        // Return formats NOT already in any selected store
        return availableFormats.filter(f => !formatsInSelectedStores.has(f.id));
    }, [availableFormats, selectedStores, displayStores]);

    // ========================================================================
    // Handlers
    // ========================================================================

    const toggleStoreSelection = useCallback((storeId: number) => {
        setSelectedStores(prev => {
            const next = new Set(prev);
            if (next.has(storeId)) {
                next.delete(storeId);
            } else {
                next.add(storeId);
            }
            return next;
        });
    }, []);

    const selectAllFiltered = useCallback(() => {
        const filteredIds = new Set(filteredStores.map(s => s.storeId));
        const allSelected = filteredStores.every(s => selectedStores.has(s.storeId));

        if (allSelected) {
            // Deselect all filtered
            setSelectedStores(prev => {
                const next = new Set(prev);
                for (const id of filteredIds) {
                    next.delete(id);
                }
                return next;
            });
        } else {
            // Select all filtered
            setSelectedStores(new Set([...selectedStores, ...filteredIds]));
        }
    }, [filteredStores, selectedStores]);

    const clearSelection = useCallback(() => {
        setSelectedStores(new Set());
        setSelectedFormats(new Set());
    }, []);

    const toggleStoreExpansion = useCallback((storeId: number) => {
        setExpandedStores(prev => {
            const next = new Set(prev);
            if (next.has(storeId)) {
                next.delete(storeId);
            } else {
                next.add(storeId);
            }
            return next;
        });
    }, []);

    const handleQuantityChange = useCallback((assignment: FormatAssignment, newQuantity: number) => {
        setEditedAssignments(prev => {
            const next = new Map(prev);
            next.set(assignment.key, { ...assignment, quantity: Math.max(0, newQuantity) });
            return next;
        });
    }, []);

    const handleOperatorChange = useCallback((assignment: FormatAssignment, operatorId: number, operatorName: string) => {
        setEditedAssignments(prev => {
            const next = new Map(prev);
            next.set(assignment.key, { ...assignment, operatorId, operatorName });
            return next;
        });
    }, []);

    // Toggle format selection
    const toggleFormatSelection = useCallback((assignmentKey: string) => {
        setSelectedFormats(prev => {
            const next = new Set(prev);
            if (next.has(assignmentKey)) {
                next.delete(assignmentKey);
            } else {
                next.add(assignmentKey);
            }
            return next;
        });
    }, []);

    // Compute total selection count (stores + independent formats)
    const selectionCount = useMemo(() => {
        let count = selectedStores.size;
        // Add formats that aren't from selected stores
        for (const key of selectedFormats) {
            let storeId: number | undefined;
            for (const store of displayStores) {
                const a = store.assignments.find(a => a.key === key);
                if (a) { storeId = a.storeId; break; }
            }
            if (storeId === undefined || !selectedStores.has(storeId)) {
                count++;
            }
        }
        return count;
    }, [selectedStores, selectedFormats, displayStores]);

    // Unified delete handler - operates on current selection
    const handleUnifiedDelete = useCallback(() => {
        if (selectionCount === 0) return;

        const newMutations: SnapshotMutation[] = [];
        const storesToRemove = new Set<number>();
        const formatsToRemove = new Set<string>();

        // Process selected stores (entire store deletion)
        for (const storeId of selectedStores) {
            const store = snapshotData?.stores.find(s => s.storeId === storeId);
            // Also check if it's a pending ADD_STORE mutation
            const pendingAddStoreMutation = pendingMutations.find(m => m.type === 'ADD_STORE' && m.storeId === storeId);

            if (!store && !pendingAddStoreMutation) continue;

            storesToRemove.add(storeId);

            if (pendingAddStoreMutation) {
                // If it's a pending ADD_STORE, remove the ADD_STORE mutation itself
                setPendingMutations(prev => prev.filter(m => !(m.type === 'ADD_STORE' && m.storeId === storeId)));
            } else if (store) {
                // If it's an existing store, create REMOVE_STORE mutations
                const packIds = new Set(store.assignments.map(a => a.packId));
                for (const packId of packIds) {
                    newMutations.push({
                        type: 'REMOVE_STORE',
                        packId,
                        storeId: store.storeId,
                        storeName: store.storeName,
                    });
                }
            }
        }

        // Process selected formats (only if store not already selected)
        for (const key of selectedFormats) {
            // Look up the actual assignment to get correct ids (key parsing breaks for negative surrogate packIds)
            let assignment: typeof displayStores[0]['assignments'][0] | undefined;
            for (const store of displayStores) {
                const a = store.assignments.find(a => a.key === key);
                if (a) { assignment = a; break; }
            }
            if (!assignment) continue;

            const { packId, storeId, formatId } = assignment;

            // Skip if store is being deleted entirely
            if (selectedStores.has(storeId)) continue;

            formatsToRemove.add(key);

            newMutations.push({
                type: 'REMOVE_FORMAT',
                packId,
                storeId,
                formatId,
            });
        }

        // Update state
        setPendingMutations(prev => [...prev, ...newMutations]);
        if (storesToRemove.size > 0) {
            setRemovedStores(prev => new Set([...prev, ...storesToRemove]));
        }
        if (formatsToRemove.size > 0) {
            setRemovedFormats(prev => new Set([...prev, ...formatsToRemove]));
        }

        // Clear selection
        setSelectedStores(new Set());
        setSelectedFormats(new Set());

        // Toast feedback
        const storeCount = storesToRemove.size;
        const formatCount = formatsToRemove.size;
        let description = '';
        if (storeCount > 0 && formatCount > 0) {
            description = `${storeCount} store${storeCount !== 1 ? 's' : ''} and ${formatCount} format${formatCount !== 1 ? 's' : ''} will be deleted.`;
        } else if (storeCount > 0) {
            description = `${storeCount} store${storeCount !== 1 ? 's' : ''} will be deleted.`;
        } else {
            description = `${formatCount} format${formatCount !== 1 ? 's' : ''} will be deleted.`;
        }

        toast({
            title: 'Deletion staged',
            description: `${description} Review and confirm to apply.`,
        });
    }, [selectionCount, selectedStores, selectedFormats, snapshotData?.stores, pendingMutations, displayStores, toast]);

    // Handle bulk add format (still needed)
    const handleBulkAddFormat = useCallback(() => {
        if (!selectedFormatToAdd || selectedStores.size === 0) return;

        const format = availableFormats.find(f => f.id === selectedFormatToAdd);
        if (!format) return;

        const newMutations: SnapshotMutation[] = [];
        const packId = snapshotData?.packContext[0]?.packId;

        if (!packId) {
            toast({
                title: 'Error',
                description: 'No pack available for adding formats',
                variant: 'destructive',
            });
            return;
        }

        for (const storeId of selectedStores) {
            newMutations.push({
                type: 'ADD_FORMAT',
                packId,
                storeId,
                formatId: selectedFormatToAdd,
                formatName: format.name,
                quantity: 1,
            });
        }

        setPendingMutations(prev => [...prev, ...newMutations]);
        setShowAddFormatModal(false);
        setSelectedFormatToAdd(null);

        toast({
            title: 'Format addition staged',
            description: `${newMutations.length} additions added to pending changes`,
        });
    }, [selectedFormatToAdd, selectedStores, availableFormats, snapshotData?.packContext, toast]);

    const handleProceedToReview = useCallback(() => {
        const mutations = buildMutations();
        if (mutations.length === 0) {
            toast({
                title: 'No Changes',
                description: 'No changes have been made to the snapshot.',
            });
            return;
        }
        diffMutation.mutate(mutations);
    }, [buildMutations, diffMutation, toast]);

    const handleConfirm = useCallback(() => {
        const mutations = buildMutations();
        applyMutation.mutate({ mutations, reason: confirmReason || undefined });
    }, [buildMutations, confirmReason, applyMutation]);

    const handleClose = useCallback(() => {
        setStage('EDIT');
        setConfirmReason('');
        setPendingMutations([]);
        setRemovedStores(new Set());
        setRemovedFormats(new Set());
        setEditedAssignments(new Map());
        setSelectedFormats(new Set());
        setDiff(null);
        onClose();
    }, [onClose]);

    // ========================================================================
    // Computed values
    // ========================================================================

    const changeCount = useMemo(() => buildMutations().length, [buildMutations]);
    const selectedCount = selectedStores.size;

    // Get unique formats in selected stores (for bulk remove)
    const formatsInSelection = useMemo(() => {
        const formatMap = new Map<number, { id: number; name: string; count: number }>();
        for (const store of displayStores) {
            if (!selectedStores.has(store.storeId)) continue;
            for (const assignment of store.assignments) {
                const existing = formatMap.get(assignment.formatId);
                if (existing) {
                    existing.count++;
                } else {
                    formatMap.set(assignment.formatId, {
                        id: assignment.formatId,
                        name: assignment.formatName,
                        count: 1,
                    });
                }
            }
        }
        return Array.from(formatMap.values());
    }, [displayStores, selectedStores]);

    // Stage indicators
    const stageIndicators = [
        { key: 'EDIT', label: 'Edit', icon: Edit3 },
        { key: 'REVIEW', label: 'Review', icon: Eye },
        { key: 'CONFIRM', label: 'Confirm', icon: CheckSquare },
    ];

    const hasGroupedView = orderedGroups.length > 0;

    const renderStoreRows = (stores: StoreEditRow[], keyPrefix?: string) => {
        return stores.map(store => {
            const storeRowKey = keyPrefix ? `${keyPrefix}:${store.storeId}` : String(store.storeId);
            return (
                <React.Fragment key={storeRowKey}>
                    {/* Store Row (Level 1) */}
                    <TableRow
                        className={`cursor-pointer ${selectedStores.has(store.storeId) ? 'bg-blue-50' : ''} ${store.status === 'mixed' ? 'bg-amber-50' : ''}`}
                    >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                                checked={selectedStores.has(store.storeId)}
                                onCheckedChange={() => toggleStoreSelection(store.storeId)}
                            />
                        </TableCell>
                        <TableCell onClick={() => toggleStoreExpansion(store.storeId)}>
                            {expandedStores.has(store.storeId) ? (
                                <ChevronDown className="h-4 w-4" />
                            ) : (
                                <ChevronRight className="h-4 w-4" />
                            )}
                        </TableCell>
                        <TableCell onClick={() => toggleStoreExpansion(store.storeId)}>
                            <div>
                                <div className="font-medium">{store.storeName}</div>
                                {store.storeAddress && (
                                    <div className="text-xs text-muted-foreground">{store.storeAddress}</div>
                                )}
                            </div>
                        </TableCell>
                        <TableCell onClick={() => toggleStoreExpansion(store.storeId)}>
                            {store.formatSummary}
                        </TableCell>
                        <TableCell onClick={() => toggleStoreExpansion(store.storeId)}>
                            {store.operatorSummary}
                        </TableCell>
                        <TableCell onClick={() => toggleStoreExpansion(store.storeId)}>
                            {store.status === 'warning' && (
                                <Badge variant="outline" className="text-amber-600 border-amber-400">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    Unassigned
                                </Badge>
                            )}
                            {store.status === 'mixed' && (
                                <Badge variant="outline" className="text-blue-600 border-blue-400">
                                    Modified
                                </Badge>
                            )}
                            {store.status === 'clean' && (
                                <Badge variant="outline" className="text-green-600 border-green-400">
                                    Clean
                                </Badge>
                            )}
                        </TableCell>
                    </TableRow>

                    {/* Expanded Detail (Level 2) */}
                    {expandedStores.has(store.storeId) && (
                        <TableRow>
                            <TableCell colSpan={6} className="bg-slate-50 p-0">
                                <div className="p-4">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-10" />
                                                <TableHead>Format</TableHead>
                                                <TableHead>Pack</TableHead>
                                                <TableHead>Operator</TableHead>
                                                <TableHead className="w-40">Quantity</TableHead>
                                                <TableHead className="w-20">Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {store.assignments.map(assignment => (
                                                <TableRow key={assignment.key} className={assignment.isModified ? 'bg-amber-50' : (selectedFormats.has(assignment.key) || selectedStores.has(store.storeId) ? 'bg-blue-50' : '')}>
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={selectedFormats.has(assignment.key) || selectedStores.has(store.storeId)}
                                                            disabled={selectedStores.has(store.storeId)}
                                                            onCheckedChange={() => toggleFormatSelection(assignment.key)}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="font-medium">
                                                        {assignment.formatName}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground text-sm">
                                                        {assignment.packName}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select
                                                            value={String(assignment.operatorId)}
                                                            onValueChange={(value) => {
                                                                const op = operatorOptions.find(o => o.id === parseInt(value));
                                                                handleOperatorChange(
                                                                    assignment,
                                                                    parseInt(value),
                                                                    op?.name || 'Unassigned'
                                                                );
                                                            }}
                                                        >
                                                            <SelectTrigger className="w-48">
                                                                <SelectValue>
                                                                    {assignment.operatorName}
                                                                </SelectValue>
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="0">Unassigned</SelectItem>
                                                                {operatorOptions.map(op => (
                                                                    <SelectItem key={op.id} value={String(op.id)}>
                                                                        {op.name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </TableCell>
                                                    <TableCell>
                                                        {isLimitedCapacityFormat(assignment.formatId) ? (
                                                            (() => {
                                                                const avail = getLimitedCapacityAvailability(store.storeId, assignment.formatId);
                                                                const bookingConfig = formatBookingById.get(assignment.formatId);
                                                                const capacity = avail?.capacity || 0;
                                                                const remainingUnits = avail?.remainingUnits
                                                                    ?? (capacity > 0
                                                                        ? Math.floor(capacity * (avail?.remainingPercent ?? 100) / 100)
                                                                        : 0);
                                                                const maxUnits = getMaxLimitedCapacityUnits({
                                                                    capacity,
                                                                    currentQuantity: assignment.quantity,
                                                                    remainingUnits,
                                                                    absoluteLimitPerStore: bookingConfig?.absoluteLimitPerStore,
                                                                });

                                                                if (bookingConfig?.bookingType === 'absolute') {
                                                                    return (
                                                                        <div className="flex flex-col gap-1">
                                                                            <div className="flex items-center gap-1">
                                                                                <Button
                                                                                    variant="outline"
                                                                                    size="icon"
                                                                                    className="h-6 w-6"
                                                                                    disabled={assignment.quantity <= 0}
                                                                                    onClick={() => handleQuantityChange(assignment, assignment.quantity - 1)}
                                                                                >
                                                                                    <Minus className="h-3 w-3" />
                                                                                </Button>
                                                                                <Input
                                                                                    type="number"
                                                                                    min={0}
                                                                                    max={maxUnits}
                                                                                    value={assignment.quantity}
                                                                                    onChange={(e) => {
                                                                                        const next = Math.max(0, Math.min(maxUnits, parseInt(e.target.value, 10) || 0));
                                                                                        handleQuantityChange(assignment, next);
                                                                                    }}
                                                                                    className="h-6 w-14 text-center"
                                                                                />
                                                                                <Button
                                                                                    variant="outline"
                                                                                    size="icon"
                                                                                    className="h-6 w-6"
                                                                                    disabled={assignment.quantity >= maxUnits}
                                                                                    onClick={() => handleQuantityChange(assignment, assignment.quantity + 1)}
                                                                                >
                                                                                    <Plus className="h-3 w-3" />
                                                                                </Button>
                                                                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                                                                    / {capacity} (max {maxUnits})
                                                                                </span>
                                                                            </div>
                                                                            {capacity > 0 && avail && (() => {
                                                                                const hint = formatLimitedCapacityAvailabilityHint({
                                                                                    capacity,
                                                                                    currentQuantity: assignment.quantity,
                                                                                    remainingUnits,
                                                                                    maxUnits,
                                                                                    includePercents: false,
                                                                                });
                                                                                return (
                                                                                    <div className="flex flex-col text-xs text-muted-foreground">
                                                                                        <span>{hint.bookedLine}</span>
                                                                                        <span>{hint.availabilityLine}</span>
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    );
                                                                }

                                                                const currentCoverage = quantityToCoveragePercent(assignment.quantity, capacity);
                                                                const bands = bookingConfig?.bands?.length
                                                                    ? bookingConfig.bands
                                                                    : undefined;
                                                                const selectedCoverage = assignment.quantity > 0
                                                                    ? nearestPopadCoverageBand(currentCoverage, bands)
                                                                    : 0;
                                                                const coverageOptions = getEditablePopadCoverageOptions({
                                                                    capacity,
                                                                    currentQuantity: assignment.quantity,
                                                                    remainingUnits,
                                                                    bands,
                                                                });

                                                                return (
                                                                    <div className="flex flex-col gap-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <Select
                                                                                value={String(selectedCoverage)}
                                                                                onValueChange={(value) => {
                                                                                    const coverage = parseInt(value, 10);
                                                                                    const newQuantity = coverageToQuantity(coverage, capacity);
                                                                                    handleQuantityChange(assignment, newQuantity);
                                                                                }}
                                                                            >
                                                                                <SelectTrigger className="w-24 h-6">
                                                                                    <SelectValue />
                                                                                </SelectTrigger>
                                                                                <SelectContent>
                                                                                    {coverageOptions.map((cov) => (
                                                                                        <SelectItem key={cov} value={String(cov)}>
                                                                                            {cov}%
                                                                                        </SelectItem>
                                                                                    ))}
                                                                                </SelectContent>
                                                                            </Select>
                                                                        </div>
                                                                        {capacity > 0 && avail && (() => {
                                                                            const hint = formatLimitedCapacityAvailabilityHint({
                                                                                capacity,
                                                                                currentQuantity: assignment.quantity,
                                                                                remainingUnits,
                                                                                maxUnits,
                                                                            });
                                                                            return (
                                                                                <div className="flex flex-col text-xs text-muted-foreground">
                                                                                    <span>{hint.bookedLine}</span>
                                                                                    <span>{hint.availabilityLine}</span>
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                );
                                                            })()
                                                        ) : (
                                                            /* Standard format: Show quantity +/- controls */
                                                            <div className="flex items-center gap-1">
                                                                <Button
                                                                    variant="outline"
                                                                    size="icon"
                                                                    className="h-6 w-6"
                                                                    onClick={() => handleQuantityChange(assignment, assignment.quantity - 1)}
                                                                >
                                                                    <Minus className="h-3 w-3" />
                                                                </Button>
                                                                <Input
                                                                    type="number"
                                                                    value={assignment.quantity}
                                                                    onChange={(e) => handleQuantityChange(assignment, parseInt(e.target.value) || 0)}
                                                                    className="h-6 w-14 text-center"
                                                                />
                                                                <Button
                                                                    variant="outline"
                                                                    size="icon"
                                                                    className="h-6 w-6"
                                                                    onClick={() => handleQuantityChange(assignment, assignment.quantity + 1)}
                                                                >
                                                                    <Plus className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {assignment.isModified && (
                                                            <Badge variant="outline" className="text-amber-600 border-amber-600">
                                                                Changed
                                                            </Badge>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </TableCell>
                        </TableRow>
                    )}
                </React.Fragment>
            );
        });
    };

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        Detailed Campaign Edit
                        <Badge variant="secondary" className="ml-2">
                            Campaign #{campaignId}
                        </Badge>
                    </DialogTitle>
                    <DialogDescription>
                        Store-centric workspace for editing campaign snapshots. Changes are staged until confirmed.
                    </DialogDescription>

                    {/* Pack Context */}
                    {snapshotData?.packContext && snapshotData.packContext.length > 0 && (
                        <div className="text-sm text-muted-foreground mt-1">
                            <Package className="inline h-4 w-4 mr-1" />
                            Originally created from: {snapshotData.packContext.map(p => p.packName).join(', ')}
                        </div>
                    )}

                    {/* Stage Indicators */}
                    <div className="flex items-center gap-2 mb-4">
                        <Separator orientation="vertical" className="h-6 mx-2" />
                        <div className="flex items-center gap-2 flex-1">
                            {stageIndicators.map((s, i) => (
                                <React.Fragment key={s.key}>
                                    <div className={`flex items-center gap-2 ${stage === s.key ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                                        <s.icon className="h-4 w-4" />
                                        {s.label}
                                    </div>
                                    {i < stageIndicators.length - 1 && (
                                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </DialogHeader>

                <Separator />

                {/* Content by stage */}
                <div className="flex-1 overflow-auto p-4">
                    {isLoadingData && (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                            <span className="ml-2">Loading snapshot data...</span>
                        </div>
                    )}

                    {dataError && (
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertTitle>Error</AlertTitle>
                            <AlertDescription>
                                {dataError instanceof Error ? dataError.message : 'Failed to load snapshot'}
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* EDIT Stage */}
                    {stage === 'EDIT' && (
                        <div className="space-y-4">
                            {/* Search Bar */}
                            <div className="flex gap-2 items-center">
                                <Search className="h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Store name..."
                                    value={searchStore}
                                    onChange={(e) => setSearchStore(e.target.value)}
                                    className="w-48"
                                />
                                <Input
                                    placeholder="Store code..."
                                    value={searchCode}
                                    onChange={(e) => setSearchCode(e.target.value)}
                                    className="w-32"
                                />
                                <Input
                                    placeholder="Town..."
                                    value={searchTown}
                                    onChange={(e) => setSearchTown(e.target.value)}
                                    className="w-32"
                                />
                                <span className="text-sm text-muted-foreground ml-2">
                                    {filteredStores.length} of {snapshotData?.stores.length} stores
                                </span>
                            </div>

                            {displayStores.length > 0 ? (
                                <>
                                    {/* Selection Bar */}
                                    <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={selectAllFiltered}
                                        >
                                            {filteredStores.every(s => selectedStores.has(s.storeId)) && filteredStores.length > 0
                                                ? `Deselect all (${filteredStores.length})`
                                                : `Select all (${filteredStores.length})`}
                                        </Button>
                                        {selectionCount > 0 && (
                                            <>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={clearSelection}
                                                >
                                                    Clear
                                                </Button>
                                                <Separator orientation="vertical" className="h-6" />
                                                <span className="text-sm font-medium">
                                                    {selectionCount} item{selectionCount !== 1 ? 's' : ''} selected
                                                </span>
                                                <Separator orientation="vertical" className="h-6" />
                                                <Button
                                                    variant="destructive"
                                                    size="sm"
                                                    onClick={handleUnifiedDelete}
                                                    disabled={selectionCount === 0}
                                                >
                                                    <Trash2 className="h-3 w-3 mr-1" />
                                                    Delete ({selectionCount})
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setShowAddFormatModal(true)}
                                                    disabled={selectedStores.size === 0}
                                                >
                                                    <Plus className="h-3 w-3 mr-1" />
                                                    Add Format
                                                </Button>
                                            </>
                                        )}
                                        <Separator orientation="vertical" className="h-6 mx-1" />
                                        {/* Add Store button - disabled for manual campaigns (no packs to clone from) */}
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-2"
                                            onClick={() => {
                                                if (snapshotData?.campaignType === 'manual') {
                                                    setIsAddStoreManualModalOpen(true);
                                                } else {
                                                    setIsAddStoreModalOpen(true);
                                                }
                                            }}
                                        >
                                            <Plus className="h-3 w-3" />
                                            Add Store
                                        </Button>
                                        <div className="ml-auto text-sm text-muted-foreground">
                                            {changeCount} pending change{changeCount !== 1 ? 's' : ''}
                                        </div>
                                    </div>

                                    {/* Store Table */}
                                    <div className="border rounded-lg overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-10" />
                                                    <TableHead className="w-10" />
                                                    <TableHead>Store</TableHead>
                                                    <TableHead>Formats</TableHead>
                                                    <TableHead>Operators</TableHead>
                                                    <TableHead>Status</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {hasGroupedView ? (
                                                    <>
                                                        {groupedStoreSections.map(({ group, stores }) => (
                                                            <React.Fragment key={group.groupKey}>
                                                                <TableRow className="bg-muted/40">
                                                                    <TableCell colSpan={6}>
                                                                        <div className="flex flex-col gap-1">
                                                                            <div className="flex items-center gap-2">
                                                                                <Package className="h-4 w-4 text-muted-foreground" />
                                                                                <span className="font-semibold">{group.groupName}</span>
                                                                                <span className="text-sm text-muted-foreground flex items-center gap-1">
                                                                                    <Users className="h-3 w-3" />
                                                                                    {group.storeIds.length} {group.storeIds.length === 1 ? 'store' : 'stores'}
                                                                                </span>
                                                                            </div>
                                                                            {group.seedPackId != null && group.seedPackName && (
                                                                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                                                                    <Info className="h-3 w-3" />
                                                                                    Originally from: {group.seedPackName}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </TableCell>
                                                                </TableRow>
                                                                {stores.length > 0 && renderStoreRows(stores, group.groupKey)}
                                                            </React.Fragment>
                                                        ))}
                                                        {ungroupedStores.length > 0 && (
                                                            <React.Fragment key="ungrouped">
                                                                <TableRow className="bg-muted/20">
                                                                    <TableCell colSpan={6}>
                                                                        <div className="flex items-center gap-2">
                                                                            <Package className="h-4 w-4 text-muted-foreground" />
                                                                            <span className="font-semibold">Ungrouped</span>
                                                                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                                                                                <Users className="h-3 w-3" />
                                                                                {ungroupedStores.length} {ungroupedStores.length === 1 ? 'store' : 'stores'}
                                                                            </span>
                                                                        </div>
                                                                    </TableCell>
                                                                </TableRow>
                                                                {renderStoreRows(ungroupedStores, 'ungrouped')}
                                                            </React.Fragment>
                                                        )}
                                                    </>
                                                ) : (
                                                    renderStoreRows(displayStores)
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground border rounded-lg bg-slate-50">
                                    <Search className="h-8 w-8 mb-2 opacity-20" />
                                    <p>No matching stores found</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* REVIEW Stage */}
                    {stage === 'REVIEW' && diff && (
                        <div className="space-y-4">
                            {/* Warnings */}
                            {diff.warnings.length > 0 && (
                                <Alert variant="destructive">
                                    <AlertTriangle className="h-4 w-4" />
                                    <AlertTitle>Warnings</AlertTitle>
                                    <AlertDescription>
                                        <ul className="list-disc list-inside">
                                            {diff.warnings.map((w, i) => (
                                                <li key={i}>{w.message}</li>
                                            ))}
                                        </ul>
                                    </AlertDescription>
                                </Alert>
                            )}

                            {/* Summary */}
                            <div className="grid grid-cols-4 gap-4">
                                <div className="p-4 border rounded-lg text-center">
                                    <div className="text-2xl font-bold">{diff.summary.storesAffected}</div>
                                    <div className="text-sm text-muted-foreground">Stores affected</div>
                                </div>
                                <div className="p-4 border rounded-lg text-center">
                                    <div className="text-2xl font-bold text-green-600">{diff.summary.totalAdded}</div>
                                    <div className="text-sm text-muted-foreground">Added</div>
                                </div>
                                <div className="p-4 border rounded-lg text-center">
                                    <div className="text-2xl font-bold text-amber-600">{diff.summary.totalModified}</div>
                                    <div className="text-sm text-muted-foreground">Modified</div>
                                </div>
                                <div className="p-4 border rounded-lg text-center">
                                    <div className="text-2xl font-bold text-red-600">{diff.summary.totalRemoved}</div>
                                    <div className="text-sm text-muted-foreground">Removed</div>
                                </div>
                            </div>

                            <p className="text-sm text-muted-foreground">
                                Changes will apply exactly as shown. Please review carefully.
                            </p>

                            {/* Changes list */}
                            <div className="border rounded-lg overflow-hidden max-h-64 overflow-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Action</TableHead>
                                            <TableHead>Store</TableHead>
                                            <TableHead>Format</TableHead>
                                            <TableHead>Change</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {diff.changes.map((change, i) => (
                                            <TableRow key={i}>
                                                <TableCell>
                                                    <Badge
                                                        variant={change.action === 'ADDED' ? 'default' : change.action === 'REMOVED' ? 'destructive' : 'secondary'}
                                                    >
                                                        {change.action}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>{change.store.name}</TableCell>
                                                <TableCell>{change.format.name}</TableCell>
                                                <TableCell>
                                                    {change.quantityOld !== change.quantityNew && (
                                                        <span className="mr-2">
                                                            Qty: {change.quantityOld ?? '-'} → {change.quantityNew ?? '-'}
                                                        </span>
                                                    )}
                                                    {change.operatorOld?.id !== change.operatorNew?.id && (
                                                        <span>
                                                            Op: {change.operatorOld?.name ?? '-'} → {change.operatorNew?.name ?? '-'}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* CONFIRM Stage */}
                    {stage === 'CONFIRM' && (
                        <div className="space-y-4">
                            <Alert>
                                <CheckCircle className="h-4 w-4" />
                                <AlertTitle>Ready to Apply</AlertTitle>
                                <AlertDescription>
                                    {diff?.summary.storesAffected} store{(diff?.summary.storesAffected || 0) !== 1 ? 's' : ''} will be updated with{' '}
                                    {(diff?.summary.totalAdded || 0) + (diff?.summary.totalModified || 0) + (diff?.summary.totalRemoved || 0)} changes.
                                </AlertDescription>
                            </Alert>

                            <div>
                                <label className="text-sm font-medium">Reason for changes (optional)</label>
                                <Textarea
                                    value={confirmReason}
                                    onChange={(e) => setConfirmReason(e.target.value)}
                                    placeholder="Enter a reason for this edit..."
                                    className="mt-1"
                                />
                            </div>
                        </div>
                    )}
                </div>

                <Separator />

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>

                    {stage === 'EDIT' && (
                        <Button
                            onClick={handleProceedToReview}
                            disabled={changeCount === 0 || diffMutation.isPending}
                        >
                            {diffMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Review Changes ({changeCount})
                        </Button>
                    )}

                    {stage === 'REVIEW' && (
                        <>
                            <Button variant="outline" onClick={() => setStage('EDIT')}>
                                Back to Edit
                            </Button>
                            <Button onClick={() => setStage('CONFIRM')}>
                                Proceed to Confirm
                            </Button>
                        </>
                    )}

                    {stage === 'CONFIRM' && (
                        <>
                            <Button variant="outline" onClick={() => setStage('REVIEW')}>
                                Back to Review
                            </Button>
                            <Button
                                onClick={handleConfirm}
                                disabled={applyMutation.isPending}
                            >
                                {applyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Apply Changes
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>

            {/* Bulk Add Format Modal */}
            <Dialog open={showAddFormatModal} onOpenChange={setShowAddFormatModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Format to {selectedCount} Stores</DialogTitle>
                        <DialogDescription>
                            Select a format to add to all selected stores.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Select
                            value={selectedFormatToAdd?.toString() || ''}
                            onValueChange={(value) => setSelectedFormatToAdd(parseInt(value))}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select format to add..." />
                            </SelectTrigger>
                            <SelectContent>
                                {formatsNotInSelection.length === 0 ? (
                                    <div className="p-2 text-sm text-muted-foreground">
                                        All available formats are already assigned to the selected store(s).
                                    </div>
                                ) : (
                                    formatsNotInSelection.map(format => (
                                        <SelectItem key={format.id} value={format.id.toString()}>
                                            {format.name}
                                        </SelectItem>
                                    ))
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddFormatModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleBulkAddFormat}
                            disabled={!selectedFormatToAdd}
                        >
                            Add Format
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {retailerId && (
                <AddStoreModal
                    isOpen={isAddStoreModalOpen}
                    onClose={() => setIsAddStoreModalOpen(false)}
                    campaignId={campaignId}
                    retailerId={retailerId}
                    existingStoreIds={new Set([
                        ...(snapshotData?.stores.map(s => s.storeId) || []),
                        ...pendingMutations.filter(m => m.type === 'ADD_STORE').map(m => m.storeId!).filter(Boolean)
                    ])}
                    packs={snapshotData?.packContext || []}
                    existingStores={snapshotData?.stores || []}
                    onConfirm={(mutation) => {
                        setPendingMutations(prev => [...prev, mutation]);
                        toast({
                            title: 'Store Added',
                            description: `${mutation.storeName} has been staged for addition.`,
                        });
                    }}
                />
            )}


            {/* Add Store Manual Modal */}
            {retailerId && (
                <AddStoreManualModal
                    isOpen={isAddStoreManualModalOpen}
                    onClose={() => setIsAddStoreManualModalOpen(false)}
                    retailerId={retailerId}
                    existingStoreIds={new Set([
                        ...(snapshotData?.stores.map(s => s.storeId) || []),
                        ...pendingMutations.filter(m => m.type === 'ADD_STORE').map(m => m.storeId!).filter(Boolean)
                    ])}
                    usedFormatIds={new Set(
                        snapshotData?.stores.flatMap(s => s.assignments.map(a => a.formatId)) || []
                    )}
                    virtualPackId={snapshotData?.packContext?.[0]?.packId || (-1 * campaignId)}
                    onConfirm={(mutation) => {
                        setPendingMutations(prev => [...prev, mutation]);
                        toast({
                            title: 'Store Added',
                            description: `${mutation.storeName} has been staged for addition.`,
                        });
                    }}
                />
            )}
        </Dialog>
    );
}

export default SnapshotEditWorkspace;
